import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  IconButton,
  Modal,
  Stack,
  Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import BusinessIcon from "@mui/icons-material/Business";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useSelector } from "react-redux";
import { Postrequestcall } from "../../request/Postrequest";
import { GET_USER_CLIENTS_ADMIN_LIST } from "../../constant/Apipath";
import Loader from "../../common/Loader";

const ClaimCompanyClientModal = ({ open, onClose, onSelectClient, companyDetails }) => {
  const { loginData } = useSelector((state) => state?.main);
  const currentUserEmail = loginData?.userInformation?.Email || "";
  const [loading, setLoading] = useState(false);
  const [clientList, setClientList] = useState([]);

  const claimCompanyBaseUrl = (import.meta.env.REACT_APP_COMPANY_WEB_URL || "").trim();


  useEffect(() => {
    let isActive = true;
    const fetchClientList = async () => {
      if (!open) return;
      try {
        setLoading(true);
        const payload = { UserEmail: currentUserEmail };
        const res = await Postrequestcall(
          GET_USER_CLIENTS_ADMIN_LIST,
          payload,
          loginData?.accessToken
        );
        const responseData = res?.data ?? res ?? {};
        const data = responseData?.data ?? responseData?.Data;
        if (isActive) setClientList(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("ClaimCompanyClientModal-> Error:", error);
        if (isActive) setClientList([]);
      } finally {
        if (isActive) setLoading(false);
      }
    };
    fetchClientList();
    return () => { isActive = false; };
  }, [open, currentUserEmail, loginData?.accessToken]);

  const sortedClientList = useMemo(() => {
    return (clientList || [])
      .slice()
      .sort((a, b) =>
        (a?.organizationName || a?.OrganizationName || "").localeCompare(
          b?.organizationName || b?.OrganizationName || ""
        )
      );
  }, [clientList]);

  const getClientId = (client) => client?.clientId ?? client?.ClientId ?? client?.id ?? 0;
  const getClientName = (client) =>
    client?.organizationName || client?.OrganizationName || client?.ClientName || client?.name || "Client";
  const getClientLogo = (client) =>
    client?.clientLogo || client?.ClientLogo || client?.Logo || client?.logo || "";

  const openClaimPortal = (clientId = 0) => {
    if (!companyDetails?.id) return;
    const email = loginData?.userInformation?.Email || "";
    const url = `${claimCompanyBaseUrl || ""}claim-company/${companyDetails.id}/${clientId || 0}/${email}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleSelect = (clientId, client = null) => {
    onSelectClient?.(clientId, client);
    openClaimPortal(clientId);
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="claim-client-modal-title">
      <Box
        className="user-modal-responsive"
        sx={{
          outline: "none",
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: "0 24px 60px rgba(0,0,0,0.14)",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            mb: 2.5,
          }}
        >
          <Box>
            <Typography
              id="claim-client-modal-title"
              sx={{
                fontSize: "1.2rem",
                fontWeight: 800,
                letterSpacing: "-0.3px",
                color: "text.primary",
                lineHeight: 1.2,
                mb: 0.4,
              }}
            >
              Select a Client
            </Typography>
            <Typography variant="body2"  sx={{color: "text.secondary",  lineHeight: 1.5 }}>
              Choose an admin client to continue the claim flow.
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            aria-label="close"
            size="small"
            sx={{
              bgcolor: "grey.100",
              "&:hover": { bgcolor: "grey.200" },
              mt: 0.25,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {loading ? (
          <Box sx={{ minHeight: 320, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Loader />
          </Box>
        ) : (
          <Stack spacing={2}>
            {/* ── Create New Client Card (hero) ── */}
            <Box
              onClick={() => handleSelect(0, null)}
              sx={{
                position: "relative",
                borderRadius: 3,
                overflow: "hidden",
                cursor: "pointer",
                background: "linear-gradient(135deg, #1a56db 0%, #3b82f6 60%, #60a5fa 100%)",
                p: 2.5,
                display: "flex",
                alignItems: "center",
                gap: 2,
                boxShadow: "0 8px 24px rgba(26,86,219,0.28)",
                transition: "transform 0.18s ease, box-shadow 0.18s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 14px 32px rgba(26,86,219,0.36)",
                },
                "&:hover .arrow-icon": {
                  transform: "translateX(4px)",
                },
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: -30,
                  right: -30,
                  width: 110,
                  height: 110,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.08)",
                  pointerEvents: "none",
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: -20,
                  right: 60,
                  width: 70,
                  height: 70,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.06)",
                  pointerEvents: "none",
                },
              }}
            >
              {/* Icon */}
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: 2.5,
                  bgcolor: "rgba(255,255,255,0.18)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  backdropFilter: "blur(4px)",
                  border: "1.5px solid rgba(255,255,255,0.28)",
                }}
              >
                <AddIcon sx={{ color: "#fff", fontSize: 26 }} />
              </Box>

              {/* Text */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    color: "#fff",
                    fontWeight: 800,
                    fontSize: "1rem",
                    letterSpacing: "-0.2px",
                    mb: 0.3,
                  }}
                >
                  Create New Client
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.78)", fontSize: "0.82rem", lineHeight: 1.4 }}
                >
                  Set up a fresh client profile and complete the claim in admin.
                </Typography>
              </Box>

              {/* Arrow */}
              <Box
                className="arrow-icon"
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  bgcolor: "rgba(255,255,255,0.18)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  transition: "transform 0.18s ease",
                  border: "1.5px solid rgba(255,255,255,0.28)",
                }}
              >
                <ArrowForwardIcon sx={{ color: "#fff", fontSize: 18 }} />
              </Box>
            </Box>

            {/* ── Divider with label ── */}
            {sortedClientList.length > 0 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{ flex: 1, height: "1px", bgcolor: "divider" }} />
                <Typography
                  variant="caption"
                  sx={{ color: "text.disabled", fontWeight: 600, whiteSpace: "nowrap", letterSpacing: "0.5px" }}
                >
                  OR PICK EXISTING
                </Typography>
                <Box sx={{ flex: 1, height: "1px", bgcolor: "divider" }} />
              </Box>
            )}

            {/* ── Client list ── */}
            {sortedClientList.length > 0 ? (
              <Stack
                spacing={0.75}
                sx={{
                  maxHeight: "44vh",
                  overflowY: "auto",
                  pr: 0.5,
                  "&::-webkit-scrollbar": { width: 4 },
                  "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
                  "&::-webkit-scrollbar-thumb": { bgcolor: "grey.300", borderRadius: 99 },
                }}
              >
                {sortedClientList.map((client) => {
                  const clientId = getClientId(client);
                  return (
                    <Box
                      key={clientId || getClientName(client)}
                      onClick={() => handleSelect(clientId, client)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        px: 1.75,
                        py: 1.25,
                        borderRadius: 2.5,
                        border: "1px solid",
                        borderColor: "grey.200",
                        cursor: "pointer",
                        bgcolor: "#fff",
                        transition: "all 0.15s ease",
                        "&:hover": {
                          borderColor: "primary.main",
                          bgcolor: "primary.50",
                          boxShadow: "0 2px 12px rgba(26,86,219,0.08)",
                        },
                        "&:hover .select-btn": {
                          bgcolor: "primary.main",
                          color: "#fff",
                          borderColor: "primary.main",
                        },
                      }}
                    >
                      <Avatar
                        src={getClientLogo(client)}
                        alt={getClientName(client)}
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1.5,
                          border: "1px solid",
                          borderColor: "grey.200",
                          flexShrink: 0,
                        }}
                      >
                        <BusinessIcon sx={{ fontSize: 20, color: "grey.400" }} />
                      </Avatar>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: "0.88rem", color: "text.primary" }} variant="body2" noWrap>
                          {getClientName(client)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "text.disabled" }} noWrap>
                          Click to select this client
                        </Typography>
                      </Box>

                      <Box
                        className="select-btn"
                        sx={{
                          flexShrink: 0,
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 99,
                          border: "1.5px solid",
                          borderColor: "grey.300",
                          color: "text.secondary",
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          transition: "all 0.15s ease",
                          letterSpacing: "0.2px",
                          userSelect: "none",
                        }}
                      >
                        Select
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            ) : (
              <Box
                sx={{
                  py: 3,
                  textAlign: "center",
                  borderRadius: 3,
                  border: "1px dashed",
                  borderColor: "grey.300",
                  bgcolor: "grey.50",
                }}
              >
                <BusinessIcon sx={{ fontSize: 32, color: "grey.300", mb: 1 }} />
                <Typography variant="subtitle2" sx={{color: "text.secondary",  fontWeight: 700 }} >
                  No admin clients found
                </Typography>
                <Typography variant="caption" sx={{ color: "text.disabled" }}>
                  Use the option above to create your first client.
                </Typography>
              </Box>
            )}
          </Stack>
        )}
      </Box>
    </Modal>
  );
};

export default ClaimCompanyClientModal;